import { useAuth } from '@/hooks/useAuth'
import { type JobPostingDetailState, useNavigator } from '@/hooks/useNavigator'
import { fontSize, fontWeight } from '@/styles/font'
import { type V1Image, type V1IconType } from 'mnvm-mypage-app-ts-if'
import styled from 'styled-components'
import { IconButton, PrimaryButton } from '@/components/base/Button'
import { LocalIconType, Icon } from '@/components/base/Icon'
import { useApp } from '@/hooks/useApp'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { AppPath } from '@/routes/routes'
import { buttonSize } from '../base/Button/Base'
import { Block } from '../base/Block'
import spaceStyle from '@/styles/space'
import { showErrorToast } from '../SnackBar/BaseSnackBar'
import { size } from '@/styles/device'
import useIsMobile from '@/hooks/useMobile'
import useIsLaptop from '@/hooks/useLaptop'
import { display, align } from '@/styles/display'
import { useState, useEffect } from 'react'

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  justify-content: space-between;
  max-width: 1080px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  flex: none;
  box-sizing: border-box;
`

export const StyledNavigationLogoSP = styled.span`
  display: none;

  @media (max-width: ${size.laptop}) {
    display: block;
    font-size: 24px;
    cursor: pointer;
  }
`

const StyledCompanyImage = styled.span`
  cursor: pointer;

  @media (max-width: ${size.mobile}) {
    display: flex;
    align-items: center;
  }
`

const StyledNavigationList = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: ${size.laptop}) {
    display: none;
  }
`

const StyledNavigationContent = styled.div`
  text-align: center;
  font-size: ${fontSize.S};
  margin: 0 16px;
  cursor: pointer;
`

const StyledAuthActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${size.laptop}) {
    display: none;
  }
`

export interface Navigation {
  name: string
  icon: V1IconType | LocalIconType
  onClick: () => void
}

export interface HeaderState {
  isNavigationListOpen: boolean
}

interface Props {
  iconImage: V1Image
  navigationListPC: Navigation[]
  onClickOpenNavigationList: () => void
  onClickCloseNavigationList: () => void
}

const Header = ({
  iconImage,
  navigationListPC,
  onClickOpenNavigationList,
  onClickCloseNavigationList
}: Props): JSX.Element => {
  const { signOut, isAuthenticated } = useAuth()
  const app = useApp()
  const nav = useNavigator()
  const isMobile = useIsMobile()
  const isLaptop = useIsLaptop()

  const executeSignOut = async (): Promise<void> => {
    const result = await signOut()
    if (result.success) {
      // 状態を破棄することで、ユーザーごとの設定値を再度読み直せるようにしています。
      location.href = AppPath.SignIn
    } else {
      showErrorToast(result.message)
    }
  }

  const AuthActionsButton = (): JSX.Element => {
    return (
      <Block margin={{ left: spaceStyle.M }}>
        <StyledAuthActions>
          <LogoutButton
            isAuthed={isAuthenticated}
            onClick={() => {
              void executeSignOut()
            }}
          />
          <LoginButton
            isAuthed={isAuthenticated}
            onClick={() => {
              void nav?.gotoSignIn()
              onClickCloseNavigationList()
            }}
          />
        </StyledAuthActions>
      </Block>
    )
  }

  const CommonMenuHeaderContainer = (): JSX.Element => {
    const { state } = useLocation()

    const [isNavigateFromList, setIsNavigateFromList] = useState(false)

    const jobPostingDetailMatch = matchPath(
      { path: AppPath.JobPostingDetail, end: true },
      location.pathname
    )

    // match only digit for param id
    const isJobPostingDetail =
      jobPostingDetailMatch != null &&
      typeof jobPostingDetailMatch.params?.id === 'string' &&
      /^\d+$/.test(jobPostingDetailMatch.params.id)

    const isJobPostingSearchResult =
      [AppPath.JobPostingSearchResult].filter((e, index) => {
        return matchPath({ path: e }, location.pathname)
      }).length > 0

    const isShowSPBackButton =
      ((isNavigateFromList && isJobPostingDetail) ||
        isJobPostingSearchResult) &&
      isMobile

    useEffect(() => {
      const fromList = (state as JobPostingDetailState)?.fromList ?? false
      setIsNavigateFromList(fromList)
    }, [state])

    return (
      <>
        <>
          {isShowSPBackButton ? (
            <>
              {isAuthenticated && (
                <IconButton
                  icon={LocalIconType.ArrowBackIos}
                  color={app.theme.mainTextColor}
                  fontSize={fontSize.XXL}
                  onClick={() => {
                    void nav?.goBack()
                  }}
                />
              )}
            </>
          ) : (
            <>
              {/* empty block to adjust space on SP */}
              {!isLaptop && <Block width={'24px'}></Block>}
            </>
          )}
        </>
        <StyledCompanyImage>
          <Link to={AppPath.Top}>
            <img
              src={iconImage.url}
              alt="company logo"
              width={iconImage.width}
              height="auto"
            />
          </Link>
        </StyledCompanyImage>
        {isAuthenticated && (
          <StyledNavigationLogoSP
            onClick={() => {
              onClickOpenNavigationList()
            }}
          >
            <Icon
              iconType={LocalIconType.Menu}
              iconStyle={{ fontSize: fontSize.XXL }}
            />
          </StyledNavigationLogoSP>
        )}
      </>
    )
  }

  const PCMenuHeaderContainer = (): JSX.Element => {
    return (
      <>
        {navigationListPC.length > 0 && (
          <StyledNavigationList>
            {navigationListPC.map((navigation: Navigation, index) => {
              return (
                <StyledNavigationContent
                  key={index}
                  onClick={() => {
                    navigation.onClick()
                    onClickCloseNavigationList()
                  }}
                >
                  {navigation.name}
                </StyledNavigationContent>
              )
            })}
          </StyledNavigationList>
        )}
        {!app.havePrivatePage || <AuthActionsButton />}
      </>
    )
  }

  return (
    <StyledContainer>
      <Block
        display={display.FLEX}
        justifyContent={
          isAuthenticated ? align.SPACE_BETWEEN : align.FLEX_START
        }
        alignItems={align.CENTER}
        width="100%"
      >
        <CommonMenuHeaderContainer />
        {isLaptop && <PCMenuHeaderContainer />}
      </Block>
    </StyledContainer>
  )
}

interface LoginButtonProps {
  onClick: () => void
  isAuthed: boolean
}

const LoginButton = (props: LoginButtonProps): JSX.Element => {
  const location = window.location
  const isHiddenLogoutButton =
    [
      AppPath.SignIn,
      AppPath.SignUpComplete,
      AppPath.SignUp,
      AppPath.Entry,
      AppPath.Term,
      AppPath.Privacy,
      AppPath.JobSeekerSchedulePreview
    ].filter((e, index) => {
      return matchPath({ path: e }, location.pathname)
    }).length > 0

  if (isHiddenLogoutButton || props.isAuthed) {
    return <></>
  }

  return (
    <PrimaryButton height={buttonSize.M} onClick={props.onClick}>
      ログイン
    </PrimaryButton>
  )
}

interface LogoutButtonProps {
  onClick: () => void
  isAuthed: boolean
}

const LogoutButton = (props: LogoutButtonProps): JSX.Element => {
  const location = window.location
  const isHiddenButton =
    [AppPath.SignUp, AppPath.JobSeekerSchedulePreview].filter((e, index) => {
      return matchPath({ path: e }, location.pathname)
    }).length > 0

  if (isHiddenButton || !props.isAuthed) {
    return <></>
  }

  return (
    <Block>
      <PrimaryButton
        fontSize={fontSize.S}
        fontWeight={fontWeight.Bold}
        height={buttonSize.M}
        width="120px"
        onClick={props.onClick}
      >
        ログアウト
      </PrimaryButton>
    </Block>
  )
}

export default Header
